import { DataInsightDTO, InsightsReportDTO } from "groopit-js/client";
import { FilterSummary } from "../FeedFilter/FilterSummary";
import React from "react";

export type InsightReportType =
  | InsightsReportDTO
  | DataInsightDTO
  | InsightReportInstructions;

export interface IInsightReportInstructions {
  id: string;
  headline: string;
  title: string;
  content: string;
  timestamp: Date;
}

export class InsightReportInstructions implements IInsightReportInstructions {
  id: string;
  headline: string;
  title: string;
  content: string;
  timestamp: Date;

  constructor(data: IInsightReportInstructions) {
    this.id = data.id;
    this.headline = data.headline;
    this.title = data.title;
    this.content = data.content;
    this.timestamp = data.timestamp;
  }
}

export const getWelcomeDataInsight = (): InsightReportInstructions => {
  return new InsightReportInstructions({
    id: "welcome-data-insight",
    headline: "Proactive data insight",
    timestamp: new Date(),
    title:
      "Proactive AI. Once there are 100 data points, Groopit AI will proactively surface patterns, changes, anomalies and potential insights as they occur.",
    content:
      "Groopit AI will automatically generate proactive data insights about changes, anomalies, trends and successes in your data and publish them directly on the timeline, as frequently as every week.\n\nThis capability requires you to have a minimum of 100 insights in your feed, and will automatically generate data insights whenever you add at least 10 new insights to your group. These data insights are published to everyone in your group and contain the following information: a 100-word summary of the insight, the data model and fields where the insight was discovered, and links to each individual insight that Groopit AI used in its analysis, with a brief summary as to why they were relevant.",
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isSummaryInsight = (report: any) => {
  return report["Discriminator"] === "InsightsReportDTO";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isDataInsight = (report: any) => {
  return report["Discriminator"] === "DataInsightDTO";
};

// TODO Figure out where these should live. This used to be its own
// component but that functionality got lifted into another component.
// These functions are relevant to both the Viewers (including full
// screen and thumbnail) and the editor
export const canManage = (
  report: InsightReportType,
  userId: string,
  isOrganizer: boolean,
) => {
  return (
    isOrganizer ||
    (report instanceof InsightsReportDTO && report.creator.id === userId)
  );
};

export const getReportTitle = (report: InsightReportType) => {
  const regexForRemovingMarkdownHeaders = /^#+\s*/;
  if (report instanceof DataInsightDTO) {
    return `${
      report.analysisType.charAt(0).toUpperCase() + report.analysisType.slice(1)
    }: ${report.title.replace(regexForRemovingMarkdownHeaders, "")}`;
  }
  return report.title.replace(regexForRemovingMarkdownHeaders, "");
};

export const getReportCreator = (report: InsightsReportDTO) => {
  return `by ${report.creator.friendlyName}`;
};

export const getReportDate = (report: InsightReportType) => {
  return report instanceof InsightReportInstructions
    ? null
    : report.timestampOverride ??
        report.timestamp.toLocaleDateString(undefined, {
          dateStyle: "long",
        });
};

export const getReportFilter = (report: InsightReportType) => {
  return report instanceof InsightReportInstructions
    ? null
    : report.sourceOverride ?? <FilterSummary filter={report.filter} />;
};

export const getReportContent = (report: InsightReportType) => {
  if (report instanceof InsightsReportDTO) {
    return report.content;
  } else if (report instanceof DataInsightDTO) {
    return `Analysis:\n${report.analysis}\n\nKey Details:\n${report.keyDetails}`;
  } else {
    return report.content;
  }
};

export const getFullReportContentAsText = (report: InsightReportType) => {
  return `${getReportTitle(report)}\n${
    report instanceof InsightsReportDTO ? `${getReportCreator(report)}\n}` : ""
  }${getReportDate(report)}\n${getReportFilter(report)}\n\n${getReportContent(
    report,
  )}`;
};

export const getReportContentForEmail = (report: InsightReportType) => {
  const fullReport = getFullReportContentAsText(report);
  const urlToReport = new URL(
    report instanceof InsightsReportDTO
      ? `/Insights/${report.id}`
      : `/Insights/Data/${report.id}`,
    document.baseURI,
  ).href;
  const linkText =
    report instanceof InsightsReportDTO
      ? "View full summary insight"
      : "View full data insight";
  if (fullReport.length > 750) {
    return `${fullReport.slice(0, 750)}...\n\n${linkText} [${urlToReport}]`;
  } else {
    return `${fullReport}\n\n${linkText} [${urlToReport}]`;
  }
};

export const emailReport = (report: InsightReportType) => {
  window.location.href = `mailto:?body=${encodeURIComponent(
    getReportContentForEmail(report),
  )}`;
};
